import { generateHTML } from '@tiptap/core';
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { CustomLink } from "@/components/ui/texteditor/extensions/CustomLink";

/* -----------------------------
 * Type
 * ----------------------------- */

type TipTapNode = {
	type?: string;
	text?: string;
	content?: TipTapNode[];
};

/* -----------------------------
 * Helper Functions
 * ----------------------------- */

function stripHtml(html: string): string {
	const el = document.createElement("div");
	el.innerHTML = html;
	return el.textContent || el.innerText || "";
}

function normalizeInput(input: unknown): TipTapNode | null {
	if (!input) return null;

	// Already an object (likely TipTap JSON)
	if (typeof input === "object") {
		return input as TipTapNode;
	}

	if (typeof input === "string") {
		// Try JSON first
		try {
			return JSON.parse(input);
		} catch {
			// HTML fallback
			if (/<[a-z][\s\S]*>/i.test(input)) {
				return { type: "text", text: stripHtml(input) };
			}

			// Plain text fallback
			return { type: "text", text: input };
		}
	}

	return null;
}

function collectText(node: TipTapNode): string {
	let text = "";

	if (node.text) {
		text += node.text;
	}

	if (Array.isArray(node.content)) {
		for (const child of node.content) {
			text += collectText(child);

			// Add spacing after block-level nodes
			if (child.type === "paragraph" || child.type === "heading") {
				text += " ";
			}
		}
	}

	return text;
}

/* -----------------------------
 * Export
 * ----------------------------- */

export const tiptapUtils = {
	extractText(input: unknown): string {
		const node = normalizeInput(input);
		if (!node) return "";

		return collectText(node).replace(/\s+/g, " ").trim();
	},

	jsonToText(input: unknown): string {
		return this.extractText(input);
	},

	jsonToHtml(input: unknown): string {
        const node = normalizeInput(input);
        if (!node) return '';
        return generateHTML(node as any, [
            StarterKit.configure({ bulletList: {}, orderedList: {}, listItem: {}, underline: false, link: false }),
            Underline,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            CustomLink.configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                defaultProtocol: "https",
                HTMLAttributes: { class: "text-blue-400" },
            }),
        ]);
    },
};
